(function () {
  const STORAGE_WEAR = 'bb_dresses_wear';
  const STORAGE_LENGTH = 'bb_dresses_length';
  const STORAGE_TAB = 'bb_dresses_carousel_tab';
  let activeFetchController = null;
  let latestFetchId = 0;
  let tabSwitchAnimationTimer = null;
  const sectionPayloadCache = new Map();
  const sectionPayloadInFlight = new Map();

  function getRoot() {
    return document.querySelector('.dress-carousel-filters');
  }

  function collectionHandleFromDom() {
    const root = getRoot();
    const h = root && root.getAttribute('data-collection-handle');
    return (h && h.trim()) || 'dresses-women';
  }

  function getWearHandleFromStorage() {
    try { return sessionStorage.getItem(STORAGE_WEAR) || ''; }
    catch { return ''; }
  }

  function normalizeWearHandle(handle) {
    if (handle === 'desa-kitsune-party-edit') return 'desa';
    return handle || '';
  }

  function setWearHandle(handle) {
    const normalizedHandle = normalizeWearHandle(handle);
    try {
      if (normalizedHandle) sessionStorage.setItem(STORAGE_WEAR, normalizedHandle);
      else sessionStorage.removeItem(STORAGE_WEAR);
    } catch { /* ignore */ }
  }

  function getTabFromStorage() {
    try {
      const t = sessionStorage.getItem(STORAGE_TAB);
      if (t === 'color' || t === 'price' || t === 'length') return t;
      return 'wear';
    } catch { return 'wear'; }
  }

  function setTab(tab) {
    try { sessionStorage.setItem(STORAGE_TAB, tab); }
    catch { /* ignore */ }
  }

  function setWearContext(handle) {
    document.documentElement.dataset.bbWearContext = handle || '';
  }

  const STORAGE_COLOR = 'bb_dresses_color';
  const STORAGE_PRICE_MIN = 'bb_dresses_price_min';
  const STORAGE_PRICE_MAX = 'bb_dresses_price_max';

  function activeWearForUi() {
    return normalizeWearHandle(getWearHandleFromStorage());
  }

  function getActiveLengthHandle() {
    try { return sessionStorage.getItem(STORAGE_LENGTH) || ''; }
    catch { return ''; }
  }

  function setActiveLengthHandle(handle) {
    try {
      if (handle) sessionStorage.setItem(STORAGE_LENGTH, handle);
      else sessionStorage.removeItem(STORAGE_LENGTH);
    } catch { /* ignore */ }
  }

  function activeLengthForUi() {
    return getActiveLengthHandle();
  }

  function getColorParamKey() {
    return 'filter.v.t.shopify.color-pattern';
  }

  function getActiveColorGid() {
    try { return sessionStorage.getItem(STORAGE_COLOR) || ''; }
    catch { return ''; }
  }

  function setActiveColorGid(gid) {
    try {
      if (gid) sessionStorage.setItem(STORAGE_COLOR, gid);
      else sessionStorage.removeItem(STORAGE_COLOR);
    } catch { /* ignore */ }
  }

  function getActivePriceRange() {
    try {
      const min = sessionStorage.getItem(STORAGE_PRICE_MIN) || '';
      const max = sessionStorage.getItem(STORAGE_PRICE_MAX) || '';
      return { min, max };
    } catch { return { min: '', max: '' }; }
  }

  function setActivePriceRange(min, max) {
    try {
      if (min || max) {
        sessionStorage.setItem(STORAGE_PRICE_MIN, min || '');
        sessionStorage.setItem(STORAGE_PRICE_MAX, max || '');
      } else {
        sessionStorage.removeItem(STORAGE_PRICE_MIN);
        sessionStorage.removeItem(STORAGE_PRICE_MAX);
      }
    } catch { /* ignore */ }
  }

  function resetSecondaryFilters() {
    setActiveColorGid('');
    setActivePriceRange('', '');
    setActiveLengthHandle('');
  }

  function buildTagPath(wearHandle, lengthHandle) {
    const tags = [];
    if (wearHandle) tags.push(wearHandle);
    if (lengthHandle) tags.push(lengthHandle);
    if (!tags.length) return '';
    return `/${tags.join('+')}`;
  }

  function buildFetchUrl(wearHandle, lengthHandle, extraParams) {
    const colHandle = collectionHandleFromDom();
    const tagPath = buildTagPath(wearHandle, lengthHandle);
    const root = getRoot();
    const sectionId = root ? root.getAttribute('data-section-id') : '';
    const params = new URLSearchParams(extraParams || '');
    params.delete('bb_wear');
    params.delete('page');
    if (sectionId) params.set('section_id', sectionId);
    return `/collections/${colHandle}${tagPath}?${params.toString()}`;
  }

  function setLoadingState(isLoading) {
    const root = getRoot();
    if (!root) return;
    const locked = Boolean(isLoading);
    root.classList.toggle('is-loading', locked);
    root.setAttribute('aria-busy', locked ? 'true' : 'false');
    document.querySelectorAll('.custom-mega-navbar').forEach(el => {
      el.classList.toggle('is-collection-filter-loading', locked);
      el.setAttribute('aria-busy', locked ? 'true' : 'false');
    });
    if (locked) {
      root.querySelectorAll('button').forEach((btn) => {
        if (btn.disabled) return;
        btn.disabled = true;
        btn.dataset.bbLoadingLocked = '1';
      });
      return;
    }
    root.querySelectorAll('button[data-bb-loading-locked="1"]').forEach((btn) => {
      btn.disabled = false;
      delete btn.dataset.bbLoadingLocked;
    });
  }

  function stopLoadingIfLatest(fetchId) {
    if (fetchId !== latestFetchId) return;
    setLoadingState(false);
  }

  function ensureSectionPayload(sectionUrl, signal) {
    if (sectionPayloadCache.has(sectionUrl)) {
      return Promise.resolve(sectionPayloadCache.get(sectionUrl));
    }
    const fetchOpts = signal ? { signal } : {};
    return fetch(sectionUrl, fetchOpts)
      .then(res => {
        if (!res.ok) throw new Error('fetch failed');
        return res.text();
      })
      .then(html => {
        const payload = extractPayloadFromHtml(html);
        sectionPayloadCache.set(sectionUrl, payload);
        if (sectionPayloadCache.size > 24) {
          const firstKey = sectionPayloadCache.keys().next().value;
          sectionPayloadCache.delete(firstKey);
        }
        return payload;
      });
  }

  function fetchSectionPayload(sectionUrl) {
    return ensureSectionPayload(sectionUrl);
  }

  function payloadProductsCount(payload) {
    if (!payload || !payload.countHtml) return null;
    const wrapper = document.createElement('div');
    wrapper.innerHTML = payload.countHtml;
    const text = (wrapper.textContent || '').replace(/\s+/g, ' ').trim();
    const match = text.match(/^(\d+)/);
    if (!match) return null;
    const count = Number(match[1]);
    return Number.isNaN(count) ? null : count;
  }

  function payloadHasAnyProduct(payload) {
    if (!payload || typeof payload.gridHtml !== 'string') return null;
    const wrapper = document.createElement('div');
    wrapper.innerHTML = payload.gridHtml;
    const hasCardProduct = Boolean(wrapper.querySelector('card-product, .card-product'));
    return hasCardProduct;
  }

  function setLengthOptionVisibility(btn, isVisible) {
    if (isVisible) {
      btn.hidden = false;
      btn.classList.remove('is-length-hidden');
      btn.removeAttribute('aria-hidden');
      btn.removeAttribute('tabindex');
      return;
    }
    btn.hidden = true;
    btn.classList.add('is-length-hidden');
    btn.setAttribute('aria-hidden', 'true');
    btn.setAttribute('tabindex', '-1');
  }

  function stripLengthOptionCounters(root) {
    root.querySelectorAll('.dress-carousel-filters__length-label').forEach((el) => {
      const raw = (el.textContent || '').trim();
      el.textContent = raw.replace(/\s*\(\d+\)\s*$/, '').trim();
    });
  }

  /** Sync length chips from label counts; always run (not tied to fetch id — avoids skipped updates). */
  function refreshLengthAvailability(root) {
    stripLengthOptionCounters(root);
    const lengthButtons = Array.from(root.querySelectorAll('[data-carousel-length]'));
    if (!lengthButtons.length) {
      const lengthTabEmpty = root.querySelector('[data-carousel-l1="length"]');
      if (lengthTabEmpty) lengthTabEmpty.hidden = true;
      return;
    }

    const results = lengthButtons.map((btn) => {
      const label = btn.querySelector('.dress-carousel-filters__length-label');
      const labelText = label ? (label.textContent || '') : '';
      const countMatch = labelText.match(/\((\d+)\)\s*$/);
      const hasProducts = countMatch ? Number(countMatch[1]) > 0 : true;
      return { btn, hasProducts };
    });

    results.forEach(({ btn, hasProducts }) => {
      setLengthOptionVisibility(btn, hasProducts);
    });

    const hasVisibleLength = results.some(({ hasProducts }) => hasProducts);
    const lengthTab = root.querySelector('[data-carousel-l1="length"]');
    if (lengthTab) {
      lengthTab.hidden = !hasVisibleLength;
    }
  }

  function syncPricePanelFromBaseFilters(fetchId, wearHandle, lengthHandle) {
    const activePrice = getActivePriceRange();
    if (!activePrice.min && !activePrice.max) return;
    const params = currentFilterParams();
    params.delete('filter.v.price.gte');
    params.delete('filter.v.price.lte');
    const sectionUrl = buildFetchUrl(wearHandle, lengthHandle, params.toString());
    fetchSectionPayload(sectionUrl)
      .then(basePayload => {
        if (fetchId !== latestFetchId) return;
        const root = getRoot();
        if (!root) return;
        const livePricePanel = root.querySelector('[data-carousel-panel="price"]');
        if (!livePricePanel || !basePayload.hasPriceTab || typeof basePayload.pricePanelHtml !== 'string') return;
        livePricePanel.innerHTML = basePayload.pricePanelHtml;
        bindPriceButtons(root);
        syncPriceButtons(root);
      })
      .catch(() => {
        /* ignore fallback price panel fetch failures */
      });
  }

  function fetchAndReplaceGrid(wearHandle, lengthHandle, extraParams) {
    const fetchId = ++latestFetchId;
    setLoadingState(true);
    const sectionUrl = buildFetchUrl(wearHandle, lengthHandle, extraParams);
    const lengthPanelBaseUrl = lengthHandle
      ? buildFetchUrl(wearHandle, '', extraParams)
      : '';
    const fetchLengthPanelBase = Boolean(
      lengthHandle && lengthPanelBaseUrl && lengthPanelBaseUrl !== sectionUrl
    );
    if (activeFetchController) {
      activeFetchController.abort();
    }
    activeFetchController = new AbortController();
    const sig = activeFetchController.signal;

    const mainPromise = ensureSectionPayload(sectionUrl, sig);
    const lengthPanelPromise = fetchLengthPanelBase
      ? ensureSectionPayload(lengthPanelBaseUrl, sig).catch(() => null)
      : Promise.resolve(null);

    Promise.all([mainPromise, lengthPanelPromise])
      .then(([payload, lengthPanelBasePayload]) => {
        if (wearHandle && payload.availableWearTags && !isKnownWearTagCsv(payload.availableWearTags, wearHandle)) {
          renderWearNotFoundState();
          setWearContext(wearHandle || '');
          document.dispatchEvent(new CustomEvent('collection:grid-updated', { bubbles: true, detail: { source: 'carousel' } }));
          activeFetchController = null;
          stopLoadingIfLatest(fetchId);
          return;
        }

        const gridContainer = document.querySelector('#ProductGridContainer');
        if (payload.gridHtml && gridContainer) {
          gridContainer.innerHTML = payload.gridHtml;
        }

        const count = document.querySelector('#ProductCount');
        if (payload.countHtml && count) {
          count.innerHTML = payload.countHtml;
        }

        const liveRoot = getRoot();
        if (liveRoot) {
          const activePrice = getActivePriceRange();
          const hasActivePrice = Boolean(activePrice.min || activePrice.max);
          liveRoot.setAttribute('data-available-wear-tags', payload.availableWearTags || '');

          const hasAnyProduct = payloadHasAnyProduct(payload);
          const activeBtn = liveRoot.querySelector('[data-carousel-l1].is-active');
          const tab = (activeBtn && activeBtn.getAttribute('data-carousel-l1')) || 'wear';
          liveRoot.querySelectorAll('[data-carousel-chip-rail]').forEach(rail => {
            const railType = rail.getAttribute('data-carousel-chip-rail');
            rail.hidden = !hasAnyProduct || railType !== tab;
          });
          validateWearChips(liveRoot, payload.availableWearTags);

          const liveColorPanel = liveRoot.querySelector('[data-carousel-panel="color"]');
          if (liveColorPanel && payload.colorPanelHtml) {
            liveColorPanel.innerHTML = payload.colorPanelHtml;
            bindColorButtons(liveRoot);
          }

          const lengthUiPayload =
            fetchLengthPanelBase && lengthPanelBasePayload ? lengthPanelBasePayload : payload;

          const liveLengthTab = liveRoot.querySelector('[data-carousel-l1="length"]');
          const liveLengthPanel = liveRoot.querySelector('[data-carousel-panel="length"]');
          if (liveLengthTab) {
            if (lengthUiPayload.hasLengthTab) {
              liveLengthTab.hidden = false;
              liveLengthTab.style.removeProperty('display');
            } else {
              liveLengthTab.hidden = true;
            }
          }
          if (liveLengthPanel && typeof lengthUiPayload.lengthPanelHtml === 'string') {
            liveLengthPanel.innerHTML = lengthUiPayload.lengthPanelHtml;
            bindLengthButtons(liveRoot);
          }

          const livePriceTab = liveRoot.querySelector('[data-carousel-l1="price"]');
          const livePricePanel = liveRoot.querySelector('[data-carousel-panel="price"]');
          if (livePriceTab) {
            if (payload.hasPriceTab || hasActivePrice) {
              livePriceTab.hidden = false;
              livePriceTab.style.removeProperty('display');
            } else {
              livePriceTab.hidden = true;
            }
          }
          if (livePricePanel) {
            if (hasActivePrice) {
              syncPricePanelFromBaseFilters(fetchId, wearHandle, lengthHandle);
            } else if (payload.hasPriceTab && typeof payload.pricePanelHtml === 'string') {
              livePricePanel.innerHTML = payload.pricePanelHtml;
              bindPriceButtons(liveRoot);
            } else if (!payload.hasPriceTab) {
              livePricePanel.innerHTML = '';
            }
          }

          if (clearInvalidPriceSelection(liveRoot)) {
            activeFetchController = null;
            fetchAndReplaceGrid(wearHandle, lengthHandle, currentFilterParams().toString());
            return;
          }

          if (clearInvalidLengthSelection(liveRoot)) {
            activeFetchController = null;
            fetchAndReplaceGrid(wearHandle, '', currentFilterParams().toString());
            return;
          }

          const lengthTab = liveRoot.querySelector('[data-carousel-l1="length"]');
          if (lengthTab && lengthTab.hidden) {
            const activeTab = liveRoot.querySelector('[data-carousel-l1].is-active');
            if (activeTab && activeTab.getAttribute('data-carousel-l1') === 'length') {
              switchTab(liveRoot, 'wear');
            }
          }

          refreshLengthAvailability(liveRoot);

          if (fetchId === latestFetchId) {
            if (clearInvalidLengthSelection(liveRoot)) {
              activeFetchController = null;
              fetchAndReplaceGrid(wearHandle, '', currentFilterParams().toString());
            } else {
              const refreshedLengthTab = liveRoot.querySelector('[data-carousel-l1="length"]');
              if (refreshedLengthTab && refreshedLengthTab.hidden) {
                const activeTab = liveRoot.querySelector('[data-carousel-l1].is-active');
                if (activeTab && activeTab.getAttribute('data-carousel-l1') === 'length') {
                  switchTab(liveRoot, 'wear');
                }
              }
            }
          }

          syncChipRailVisibility(liveRoot);
          updateRailWidths(liveRoot);
        }

        setWearContext(wearHandle || '');
        document.dispatchEvent(new CustomEvent('collection:grid-updated', { bubbles: true, detail: { source: 'carousel' } }));
        activeFetchController = null;
        stopLoadingIfLatest(fetchId);
      })
      .catch((err) => {
        if (err && err.name === 'AbortError') {
          stopLoadingIfLatest(fetchId);
          return;
        }
        /* silent fail — grid stays as-is */
        activeFetchController = null;
        stopLoadingIfLatest(fetchId);
      });
  }

  function primeSectionPayload(sectionUrl) {
    if (!sectionUrl || sectionPayloadCache.has(sectionUrl) || sectionPayloadInFlight.has(sectionUrl)) return;
    const req = fetch(sectionUrl)
      .then(res => {
        if (!res.ok) throw new Error('prefetch failed');
        return res.text();
      })
      .then(html => {
        const payload = extractPayloadFromHtml(html);
        sectionPayloadCache.set(sectionUrl, payload);
        if (sectionPayloadCache.size > 24) {
          const firstKey = sectionPayloadCache.keys().next().value;
          sectionPayloadCache.delete(firstKey);
        }
      })
      .catch(() => {
        /* ignore prefetch failures */
      })
      .finally(() => {
        sectionPayloadInFlight.delete(sectionUrl);
      });
    sectionPayloadInFlight.set(sectionUrl, req);
  }

  function prefetchColorFilter(gid) {
    if (!gid) return;
    const params = currentFilterParams();
    const key = getColorParamKey();
    params.delete(key);
    params.set(key, gid);
    const wearHandle = activeWearForUi();
    const lengthHandle = activeLengthForUi();
    const url = buildFetchUrl(wearHandle, lengthHandle, params.toString());
    primeSectionPayload(url);
  }

  function extractPayloadFromHtml(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const root = getRoot();
    const sectionId = root ? root.getAttribute('data-section-id') : '';

    let updatedGridContainer = parsed.querySelector('#ProductGridContainer');
    if (!updatedGridContainer) {
      const sec = parsed.getElementById(`shopify-section-${sectionId}`);
      if (sec) updatedGridContainer = sec.querySelector('#ProductGridContainer');
    }
    const updatedCount = parsed.querySelector('#ProductCount');
    const parsedRoot = parsed.querySelector('.dress-carousel-filters');
    const parsedColorPanel = parsedRoot ? parsedRoot.querySelector('[data-carousel-panel="color"]') : null;
    const parsedLengthPanel = parsedRoot ? parsedRoot.querySelector('[data-carousel-panel="length"]') : null;
    const parsedPricePanel = parsedRoot ? parsedRoot.querySelector('[data-carousel-panel="price"]') : null;
    const parsedLengthTab = parsedRoot ? parsedRoot.querySelector('[data-carousel-l1="length"]') : null;
    const parsedPriceTab = parsedRoot ? parsedRoot.querySelector('[data-carousel-l1="price"]') : null;
    const hasAnyLengthChip = Boolean(
      parsedLengthPanel && parsedLengthPanel.querySelector('[data-carousel-length]')
    );

    return {
      gridHtml: updatedGridContainer ? updatedGridContainer.innerHTML : '',
      countHtml: updatedCount ? updatedCount.innerHTML : '',
      colorPanelHtml: parsedColorPanel ? parsedColorPanel.innerHTML : '',
      lengthPanelHtml: parsedLengthPanel ? parsedLengthPanel.innerHTML : '',
      pricePanelHtml: parsedPricePanel ? parsedPricePanel.innerHTML : '',
      hasLengthTab: Boolean(parsedLengthTab && !parsedLengthTab.hasAttribute('hidden') && hasAnyLengthChip),
      hasPriceTab: Boolean(parsedPriceTab),
      availableWearTags: parsedRoot ? (parsedRoot.getAttribute('data-available-wear-tags') || '') : ''
    };
  }

  function isKnownWearTag(root, wearHandle) {
    const raw = root.getAttribute('data-available-wear-tags') || '';
    return isKnownWearTagCsv(raw, wearHandle);
  }

  function isKnownWearTagCsv(raw, wearHandle) {
    if (!raw) return true;
    const tags = raw.split(',').map(v => v.trim()).filter(Boolean);
    return tags.includes(wearHandle);
  }

  function renderWearNotFoundState() {
    const gridContainer = document.querySelector('#ProductGridContainer');
    if (gridContainer) {
      gridContainer.innerHTML = '<h3 class="collection__title text-center">Item not found</h3>';
    }
    const count = document.querySelector('#ProductCount');
    if (count) {
      count.innerHTML = '<div class="facets__product-count">0 products</div>';
    }
  }

  /**
   * Query string for section fetches: start from the live URL (sidebar facets, sort, etc.),
   * then overlay carousel-only color/price from session when those are active.
   */
  function currentFilterParams() {
    const params = new URLSearchParams(window.location.search);
    params.delete('section_id');
    params.delete('page');

    const colorKey = getColorParamKey();
    const colorGid = getActiveColorGid();
    if (colorGid) {
      params.delete(colorKey);
      params.set(colorKey, colorGid);
    }

    const price = getActivePriceRange();
    if (price.min || price.max) {
      params.delete('filter.v.price.gte');
      params.delete('filter.v.price.lte');
      if (price.min) params.set('filter.v.price.gte', price.min);
      if (price.max) params.set('filter.v.price.lte', price.max);
    }

    return params;
  }

  function setColorFilter(gid) {
    const key = getColorParamKey();
    const cur = getActiveColorGid();
    const params = currentFilterParams();
    params.delete(key);

    if (gid && cur !== gid) {
      setActiveColorGid(gid);
      params.append(key, gid);
    } else {
      setActiveColorGid('');
    }

    const uiRoot = getRoot();
    if (uiRoot) {
      syncColorButtons(uiRoot);
      updateTabIndicators(uiRoot);
    }

    const wearHandle = activeWearForUi();
    const lengthHandle = activeLengthForUi();
    fetchAndReplaceGrid(wearHandle, lengthHandle, params.toString());
  }

  function syncColorButtons(root) {
    const active = getActiveColorGid();
    root.querySelectorAll('[data-carousel-color]').forEach(btn => {
      const v = btn.getAttribute('data-carousel-color');
      btn.classList.toggle('is-active', active === v);
    });
  }

  function syncLengthButtons(root) {
    const active = activeLengthForUi();
    root.querySelectorAll('[data-carousel-length]').forEach(btn => {
      const v = btn.getAttribute('data-carousel-length');
      btn.classList.toggle('is-active', active === v);
    });
  }

  function syncWearButtons(root) {
    const w = activeWearForUi();
    root.querySelectorAll('[data-carousel-wear]').forEach(btn => {
      const h = btn.getAttribute('data-carousel-wear');
      btn.classList.toggle('is-active', w === h);
    });
    root.querySelectorAll('[data-carousel-clear-wear]').forEach(btn => {
      const isActive = !w;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  function syncPriceButtons(root) {
    const price = getActivePriceRange();
    const isAllActive = !price.min && !price.max;
    root.querySelectorAll('[data-carousel-price-clear]').forEach(btn => {
      btn.classList.toggle('is-active', isAllActive);
      btn.setAttribute('aria-pressed', isAllActive ? 'true' : 'false');
    });
    root.querySelectorAll('[data-carousel-price-min]').forEach(btn => {
      const min = btn.getAttribute('data-carousel-price-min');
      const max = btn.getAttribute('data-carousel-price-max');
      btn.classList.toggle('is-active', price.min === min && price.max === max);
    });
  }

  function clearInvalidPriceSelection(root) {
    const price = getActivePriceRange();
    if (!price.min && !price.max) return false;
    const chips = root.querySelectorAll('[data-carousel-price-min]');
    if (!chips.length) {
      setActivePriceRange('', '');
      return true;
    }
    const ok = Array.from(chips).some(btn =>
      btn.getAttribute('data-carousel-price-min') === price.min &&
      btn.getAttribute('data-carousel-price-max') === price.max
    );
    if (ok) return false;
    setActivePriceRange('', '');
    return true;
  }

  function syncWearChipShadowVisibility(root) {
    const wearChipShadow = root.querySelector('[data-carousel-chip-rail="wear"]');
    if (!wearChipShadow) return;
    const gridContainer = document.querySelector('#ProductGridContainer');
    const hasProducts = gridContainer && gridContainer.querySelector('card-product, .card-product');
    const activeBtn = root.querySelector('[data-carousel-l1].is-active');
    const tab = (activeBtn && activeBtn.getAttribute('data-carousel-l1')) || 'wear';
    wearChipShadow.hidden = !hasProducts || tab !== 'wear';
  }

  function clearInvalidLengthSelection(root) {
    const length = activeLengthForUi();
    if (!length) return false;
    const matchingOption = root.querySelector(`[data-carousel-length="${length}"]`);
    if (
      matchingOption &&
      !matchingOption.hidden &&
      !matchingOption.classList.contains('is-length-hidden')
    ) {
      return false;
    }
    setActiveLengthHandle('');
    return true;
  }

  function wearLabelFromHandle(root, handle) {
    if (!handle) return '';
    const btn = root.querySelector(`[data-carousel-wear="${handle}"]`);
    if (!btn) return '';
    return (btn.textContent || '').trim();
  }

  function updateTabIndicators(root) {
    const wearHandle = activeWearForUi();
    const colorGid = getActiveColorGid();
    const lengthHandle = activeLengthForUi();
    const price = getActivePriceRange();
    const hasPrice = Boolean(price.min || price.max);

    const wearTab = root.querySelector('[data-carousel-l1="wear"]');
    const colorTab = root.querySelector('[data-carousel-l1="color"]');
    const lengthTab = root.querySelector('[data-carousel-l1="length"]');
    const priceTab = root.querySelector('[data-carousel-l1="price"]');

    if (wearTab) {
      const wearLabel = wearLabelFromHandle(root, wearHandle);
      wearTab.classList.toggle('has-filter', Boolean(wearHandle));
      wearTab.setAttribute('aria-label', wearLabel ? `What to wear, selected ${wearLabel}` : 'What to wear');
      const valueNode = wearTab.querySelector('.dress-carousel-filters__tab-value');
      if (valueNode) valueNode.remove();
    }

    if (colorTab) {
      colorTab.classList.toggle('has-filter', Boolean(colorGid));
    }

    if (lengthTab) {
      lengthTab.classList.toggle('has-filter', Boolean(lengthHandle));
    }

    if (priceTab) {
      priceTab.classList.toggle('has-filter', hasPrice);
    }
  }

  /** Wear chip rail: hide unless “What to wear” tab is active (keeps layout/focus consistent after AJAX). */
  function syncChipRailVisibility(root) {
    const chipRails = root.querySelectorAll('[data-carousel-chip-rail]');
    if (!chipRails.length) return;
    const activeBtn = root.querySelector('[data-carousel-l1].is-active');
    const tab = (activeBtn && activeBtn.getAttribute('data-carousel-l1')) || 'wear';
    chipRails.forEach(rail => {
      const railType = rail.getAttribute('data-carousel-chip-rail');
      rail.hidden = railType !== tab;
    });
  }

  /** Recalculate visible chip count and update rail width */
  function updateRailWidths(root) {
    root.querySelectorAll('[data-carousel-chip-rail]').forEach(rail => {
      // Count visible buttons (not hidden, not display:none)
      const visibleChips = Array.from(rail.querySelectorAll('button')).filter(btn => {
        return !btn.hidden && btn.style.display !== 'none' && btn.offsetParent !== null;
      });
      const chipCount = visibleChips.length;
      if (chipCount > 0) {
        rail.style.setProperty('--dress-wear-chip-count', String(chipCount));
      }
    });
  }

  /** Hide wear chips that have no matching products */
  function validateWearChips(root, availableWearTagsCsv) {
    let availableTags;
    if (availableWearTagsCsv) {
      availableTags = new Set(availableWearTagsCsv.split(',').map(t => t.trim()).filter(Boolean));
    } else {
      // Fallback: read from root data attribute
      const raw = root.getAttribute('data-available-wear-tags') || '';
      availableTags = new Set(raw.split(',').map(t => t.trim()).filter(Boolean));
    }

    // If no tags found, skip validation so we don't hide everything
    if (availableTags.size === 0) return;

    root.querySelectorAll('[data-carousel-wear]').forEach(btn => {
      const wearHandle = btn.getAttribute('data-carousel-wear');
      const hasMatch = availableTags.has(wearHandle);
      const wrapper = btn.closest('.dress-carousel-filters__wear');
      if (wrapper) {
        wrapper.style.display = hasMatch ? '' : 'none';
      } else {
        btn.style.display = hasMatch ? '' : 'none';
      }
    });
  }

  function triggerTabSwitchAnimation(root) {
    root.classList.add('is-tab-switching');
    if (tabSwitchAnimationTimer) {
      clearTimeout(tabSwitchAnimationTimer);
    }
    tabSwitchAnimationTimer = setTimeout(() => {
      root.classList.remove('is-tab-switching');
      tabSwitchAnimationTimer = null;
    }, 1700);
  }

  function switchTab(root, tab, opts) {
    const animate = Boolean(opts && opts.animate);
    setTab(tab);
    if (animate) {
      triggerTabSwitchAnimation(root);
    }
    root.querySelectorAll('[data-carousel-l1]').forEach(btn => {
      const active = btn.getAttribute('data-carousel-l1') === tab;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
      btn.setAttribute('tabindex', active ? '0' : '-1');
    });
    root.querySelectorAll('[data-carousel-panel]').forEach(panel => {
      const show = panel.getAttribute('data-carousel-panel') === tab;
      panel.classList.toggle('is-visible', show);
    });
    syncChipRailVisibility(root);
    if (tab === 'wear') {
      validateWearChips(root);
    }
    updateRailWidths(root);
  }

  function onWearSelect(root, handle) {
    const prev = activeWearForUi();
    resetSecondaryFilters();
    syncColorButtons(root);
    syncPriceButtons(root);
    const params = currentFilterParams();
    if (prev === handle) {
      setWearHandle('');
      window.__bbActiveWearHandle = '';
      setWearContext('');
      syncWearButtons(root);
      updateTabIndicators(root);
      fetchAndReplaceGrid('', activeLengthForUi(), params.toString());
      return;
    }
    setWearHandle(handle);
    window.__bbActiveWearHandle = handle;
    setWearContext(handle);
    syncWearButtons(root);
    updateTabIndicators(root);
    fetchAndReplaceGrid(handle, activeLengthForUi(), params.toString());
  }

  function onLengthSelect(root, handle) {
    const prev = activeLengthForUi();
    const next = prev === handle ? '' : handle;
    setActiveLengthHandle(next);
    syncLengthButtons(root);
    updateTabIndicators(root);
    fetchAndReplaceGrid(activeWearForUi(), next, currentFilterParams().toString());
  }

  function init() {
    const root = getRoot();
    if (!root) return;

    const gridContainer = document.querySelector('#ProductGridContainer');
    const hasInitialProducts = gridContainer && gridContainer.querySelector('card-product, .card-product');
    const initialTab = getTabFromStorage();
    root.querySelectorAll('[data-carousel-chip-rail]').forEach(rail => {
      const railType = rail.getAttribute('data-carousel-chip-rail');
      rail.hidden = !hasInitialProducts || railType !== initialTab;
    });

    const initialWear = normalizeWearHandle(getWearHandleFromStorage());
    const initialColor = getActiveColorGid();
    const initialLength = activeLengthForUi();
    if (initialWear || initialColor || initialLength) {
      if (initialWear) {
        setWearContext(initialWear);
        window.__bbActiveWearHandle = initialWear;
      }
      fetchAndReplaceGrid(initialWear, initialLength, currentFilterParams().toString());
    }

    switchTab(root, getTabFromStorage());
    updateRailWidths(root);
    validateWearChips(root);
    syncColorButtons(root);
    syncWearButtons(root);
    syncLengthButtons(root);
    syncPriceButtons(root);
    updateTabIndicators(root);

    root.querySelectorAll('[data-carousel-l1]').forEach(btn => {
      btn.addEventListener('click', () => {
        switchTab(root, btn.getAttribute('data-carousel-l1'), { animate: true });
      });
    });

    root.querySelectorAll('[data-carousel-wear]').forEach(btn => {
      btn.addEventListener('click', () => {
        switchTab(root, 'wear');
        onWearSelect(root, btn.getAttribute('data-carousel-wear'));
      });
    });

    root.querySelectorAll('[data-carousel-clear-wear]').forEach(btn => {
      btn.addEventListener('click', () => {
        switchTab(root, 'wear');
        resetSecondaryFilters();
        setWearHandle('');
        window.__bbActiveWearHandle = '';
        setWearContext('');
        syncColorButtons(root);
        syncLengthButtons(root);
        syncPriceButtons(root);
        syncWearButtons(root);
        updateTabIndicators(root);
        fetchAndReplaceGrid('', activeLengthForUi(), currentFilterParams().toString());
      });
    });

    bindColorButtons(root);
    bindLengthButtons(root);
    bindPriceButtons(root);
    refreshLengthAvailability(root);
    if (clearInvalidLengthSelection(root)) {
      fetchAndReplaceGrid(activeWearForUi(), '', currentFilterParams().toString());
    }

    document.addEventListener('collection:grid-updated', (ev) => {
      const src = ev.detail && ev.detail.source;
      const r = getRoot();

      if (src === 'facets' && r) {
        sectionPayloadCache.clear();
        fetchAndReplaceGrid(activeWearForUi(), activeLengthForUi(), currentFilterParams().toString());
        return;
      }

      if (!r) return;

      if (src === 'carousel') {
        refreshLengthAvailability(r);
        syncWearChipShadowVisibility(r);
        updateRailWidths(r);
        validateWearChips(r);
        if (clearInvalidLengthSelection(r)) {
          fetchAndReplaceGrid(activeWearForUi(), '', currentFilterParams().toString());
          return;
        }
        syncColorButtons(r);
        syncWearButtons(r);
        syncLengthButtons(r);
        syncPriceButtons(r);
        updateTabIndicators(r);
        return;
      }

      refreshLengthAvailability(r);
      syncWearChipShadowVisibility(r);
      updateRailWidths(r);
      validateWearChips(r);
      if (clearInvalidLengthSelection(r)) {
        fetchAndReplaceGrid(activeWearForUi(), '', currentFilterParams().toString());
        return;
      }
      syncColorButtons(r);
      syncWearButtons(r);
      syncLengthButtons(r);
      syncPriceButtons(r);
      updateTabIndicators(r);
    });
  }

  function bindPriceButtons(root) {
    root.querySelectorAll('[data-carousel-price-min]').forEach(btn => {
      if (btn.dataset.bbPriceBound === '1') return;
      btn.dataset.bbPriceBound = '1';
      btn.addEventListener('click', () => {
        switchTab(root, 'price');
        const min = btn.getAttribute('data-carousel-price-min');
        const max = btn.getAttribute('data-carousel-price-max');
        const cur = getActivePriceRange();
        if (cur.min === min && cur.max === max) {
          setActivePriceRange('', '');
        } else {
          setActivePriceRange(min, max);
        }
        syncPriceButtons(root);
        updateTabIndicators(root);
        fetchAndReplaceGrid(activeWearForUi(), activeLengthForUi(), currentFilterParams().toString());
      });
    });
    root.querySelectorAll('[data-carousel-price-clear]').forEach(btn => {
      if (btn.dataset.bbPriceClearBound === '1') return;
      btn.dataset.bbPriceClearBound = '1';
      btn.addEventListener('click', () => {
        switchTab(root, 'price');
        setActivePriceRange('', '');
        syncPriceButtons(root);
        updateTabIndicators(root);
        fetchAndReplaceGrid(activeWearForUi(), activeLengthForUi(), currentFilterParams().toString());
      });
    });
  }

  function bindLengthButtons(root) {
    root.querySelectorAll('[data-carousel-length]').forEach(btn => {
      if (btn.dataset.bbLengthBound === '1') return;
      btn.dataset.bbLengthBound = '1';
      btn.addEventListener('click', () => {
        switchTab(root, 'length');
        const handle = btn.getAttribute('data-carousel-length') || '';
        onLengthSelect(root, handle);
      });
    });
    stripLengthOptionCounters(root);
  }

  function bindColorButtons(root) {
    root.querySelectorAll('[data-carousel-color]').forEach(btn => {
      if (btn.dataset.bbColorBound === '1') return;
      btn.dataset.bbColorBound = '1';
      const prefetch = () => {
        if (btn.disabled) return;
        const gid = btn.getAttribute('data-carousel-color');
        prefetchColorFilter(gid);
      };
      btn.addEventListener('pointerenter', prefetch);
      btn.addEventListener('focus', prefetch);
      btn.addEventListener('touchstart', prefetch, { passive: true });
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        switchTab(root, 'color');
        const gid = btn.getAttribute('data-carousel-color');
        setColorFilter(gid);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
